import { useCallback, useEffect, useState } from "react";

type Options<T> = {
    initialData: T;
    skip?: boolean;
    onError?: (error: unknown) => void;
};

type Result<T> = {
    token: string | null;
    data: T;
    isError: boolean;
};

/**
 * Fetches whatever `key` describes and keeps the result tagged with the request it answers, so `isLoading` is
 * derived during render instead of being set from an effect. A response for a request that is no longer current
 * is dropped. A failed request keeps the previous data and reports `isError`.
 *
 * `key` is compared by value (`JSON.stringify`), so a payload object rebuilt on every render does not refetch.
 * It must contain everything `fetcher` reads.
 */
const useFetchResult = <T>(key: unknown, fetcher: (signal: AbortSignal) => Promise<T>, options: Options<T>) => {
    const { initialData, skip = false, onError } = options;
    const [refreshKey, setRefreshKey] = useState(0);
    const [result, setResult] = useState<Result<T>>({ token: null, data: initialData, isError: false });

    const token = JSON.stringify([key ?? null, refreshKey]);
    const isLoading = result.token !== token;

    useEffect(() => {
        if (skip) {
            return;
        }

        const controller = new AbortController();

        fetcher(controller.signal)
            .then((data) => {
                if (!controller.signal.aborted) {
                    setResult({ token, data, isError: false });
                }
            })
            .catch((error: unknown) => {
                if (controller.signal.aborted) {
                    return;
                }
                onError?.(error);
                setResult((prev) => ({ token, data: prev.data, isError: true }));
            });

        return () => controller.abort();
        // `token` identifies the request. `fetcher` and `onError` are new closures on every render, so depending on
        // them would refetch on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, skip]);

    const refetch = useCallback(() => setRefreshKey((previous) => previous + 1), []);

    const setData = useCallback((update: (previous: T) => T) => {
        setResult((prev) => ({ ...prev, data: update(prev.data) }));
    }, []);

    return { data: result.data, isLoading, isError: !isLoading && result.isError, refetch, setData };
};

export default useFetchResult;
